import { type InfiniteData, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ImageOff } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { ApiError } from "../../../api/client";
import {
  type ConversationList,
  conversationQuery,
  type SmsConversation,
  type SmsMessage,
  threadQuery,
} from "../../../api/messaging";
import { RegistrationPill } from "../../../components/messaging/RegistrationPill";
import { Badge, Card, cx, EmptyState, ErrorState, Loading } from "../../../components/ui";
import { useConversationName } from "../../../lib/crumbLabels";
import { level } from "../../../lib/crumbs";
import { fmtDate, fmtDateTime, fmtPhone } from "../../../lib/format";
import { MESSAGE_STATUS } from "../../../lib/messaging";

export const Route = createFileRoute("/admin/messages/$conversationId")({
  staticData: {
    crumb: {
      label: "Conversation",
      useLabel: ({ params }) => useConversationName(params.conversationId ?? ""),
      parents: [level("Messages", "/admin/messages")],
    },
  },
  loader: ({ context, params }) => context.queryClient.prefetchQuery(threadQuery(params.conversationId)),
  component: ThreadPage,
});

/** The header: from a cached conversations list when there is one, else fetched by id. */
function useConversation(id: string): SmsConversation | undefined {
  const qc = useQueryClient();
  const cached = qc
    .getQueriesData<InfiniteData<ConversationList>>({ queryKey: ["admin", "messages", "conversations"] })
    .flatMap(([, d]) => d?.pages.flatMap((p) => p.items) ?? [])
    .find((c) => c.id === id);
  const q = useQuery({ ...conversationQuery(id), enabled: !cached });
  return cached ?? q.data;
}

function ThreadPage() {
  const { conversationId } = Route.useParams();
  const q = useQuery(threadQuery(conversationId));
  const conv = useConversation(conversationId);
  if (q.isPending) return <Loading />;
  if (q.isError) {
    const notFound = q.error instanceof ApiError && q.error.status === 404;
    return (
      <>
        {notFound ? (
          <EmptyState title="This conversation doesn't exist" />
        ) : (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        )}
      </>
    );
  }
  return (
    <>
      {conv ? <ThreadHeader conv={conv} /> : null}
      <Thread messages={q.data.items} />
    </>
  );
}

function ThreadHeader({ conv }: { conv: SmsConversation }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1">
      <h2 className="font-medium text-heading">{conv.contact?.name ?? fmtPhone(conv.contact_number)}</h2>
      {conv.contact?.company ? <span className="text-body text-ink-2">{conv.contact.company}</span> : null}
      <span className="tnum text-ui text-ink-2">{fmtPhone(conv.contact_number)}</span>
      {conv.opted_out ? <Badge tone="danger">Opted out</Badge> : null}
      <span className="ml-auto flex items-center gap-2 text-caption text-ink-3">
        <span>
          {conv.assigned_to?.name ?? "Unassigned"} on{" "}
          <span className="tnum">{fmtPhone(conv.our_number)}</span>
          {conv.brand ? `, ${conv.brand}` : ""}
        </span>
        <RegistrationPill state={conv.texting_state} size="sm" />
      </span>
    </div>
  );
}

function dayKey(iso: string): string {
  return iso.slice(0, 10);
}

function Thread({ messages }: { messages: SmsMessage[] }) {
  const box = useRef<HTMLDivElement>(null);
  // Open at the newest text, and stay there while images load above it.
  const toEnd = useCallback(() => {
    const el = box.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);
  useEffect(() => toEnd(), [toEnd]);
  const groups = useMemo(() => {
    const out: { day: string; items: SmsMessage[] }[] = [];
    for (const m of messages) {
      const d = dayKey(m.created_at);
      const last = out[out.length - 1];
      if (last && last.day === d) last.items.push(m);
      else out.push({ day: d, items: [m] });
    }
    return out;
  }, [messages]);

  return (
    <Card>
      <div ref={box} className="flex h-[min(70vh,720px)] flex-col gap-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 ? (
          <EmptyState title="No texts in this conversation" />
        ) : (
          groups.map((g) => (
            <section key={g.day} aria-label={fmtDate(g.day)} className="flex flex-col gap-1.5">
              <p className="my-2 text-center text-caption text-ink-3">{fmtDate(g.day)}</p>
              {g.items.map((m) => (
                <Bubble key={m.id} m={m} onImageLoad={toEnd} />
              ))}
            </section>
          ))
        )}
      </div>
    </Card>
  );
}

function Bubble({ m, onImageLoad }: { m: SmsMessage; onImageLoad: () => void }) {
  if (m.kind === "event") {
    return <p className="my-1 text-center text-caption text-ink-3">{m.body}</p>;
  }
  const out = m.direction === "outbound";
  const status = MESSAGE_STATUS[m.status];
  const who = out ? (m.kind === "auto_reply" ? "Automatic reply" : (m.sent_by?.name ?? "")) : "";
  return (
    <div
      className={cx("flex max-w-[min(32rem,80%)] flex-col gap-1", out ? "items-end self-end" : "self-start")}
    >
      {m.media.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {m.media.map((x) =>
            x.url ? (
              <a key={x.id} href={x.url} target="_blank" rel="noreferrer" className="block">
                <img
                  src={x.url}
                  alt="Attached"
                  onLoad={onImageLoad}
                  className="max-h-56 max-w-56 rounded-lg object-cover shadow-material"
                />
              </a>
            ) : (
              <span
                key={x.id}
                className="flex size-24 items-center justify-center rounded-lg bg-well text-ink-3"
                title="Image not available"
              >
                <ImageOff className="size-5" />
              </span>
            ),
          )}
        </div>
      ) : null}
      {m.body ? (
        <p
          className={cx(
            "whitespace-pre-wrap break-words rounded-xl px-3 py-2 text-body",
            out ? "bg-accent-soft text-ink" : "bg-surface-2 text-ink",
            m.status === "failed" && "shadow-[inset_0_0_0_1px_var(--vd-danger)]",
          )}
        >
          {m.body}
        </p>
      ) : null}
      <p className="flex items-center gap-1.5 text-micro text-ink-3">
        {who ? <span>{who}</span> : null}
        <span className="tnum" title={fmtDateTime(m.created_at)}>
          {new Date(m.created_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
        </span>
        {out ? (
          <span className={m.status === "failed" ? "text-danger-ink" : undefined}>{status.label}</span>
        ) : null}
        {m.segments > 1 ? <span className="tnum">{m.segments} parts</span> : null}
      </p>
      {m.status === "failed" && m.error_message ? (
        <p className="text-caption text-danger-ink">
          {m.error_message}
          {m.error_code ? <span className="ml-1 font-mono text-micro text-ink-3">{m.error_code}</span> : null}
        </p>
      ) : null}
    </div>
  );
}
